<template>
    <div :class="['code-viewer', {'code-viewer--fill': fill}]">
        <div
            v-for="(line, index) in highlightedLines"
            :key="index"
            class="code-viewer__line"
        >
            <span class="code-viewer__gutter">{{ index + 1 }}</span>
            <span
                class="code-viewer__code"
                v-html="line || '&nbsp;'"
            ></span>
        </div>
    </div>
</template>

<script>
export default {
    name: "CodeViewer",
    props: {
        code: {
            type: String,
            default: '',
        },
        language: {
            type: String,
            default: 'text',
        },
        fill: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        lines() {
            const normalized = (this.code || '').replace(/\r\n/g, '\n');
            return normalized.split('\n');
        },
        highlightedLines() {
            return this.lines.map((line) => this.highlightLine(line));
        },
    },
    methods: {
        highlightLine(line) {
            const escaped = this.escapeHtml(line);
            if (this.language === 'json') {
                return this.highlightJson(escaped);
            }
            if (this.language === 'xml') {
                return this.highlightXml(escaped);
            }

            return escaped;
        },
        escapeHtml(value) {
            return value
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;');
        },
        highlightJson(value) {
            return value.replace(
                /("(?:\\u[a-fA-F0-9]{4}|\\[^u]|[^\\"])*"(?:\s*:)?|\btrue\b|\bfalse\b|\bnull\b|-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?|[{}\[\],:])/g,
                (match) => {
                    let className = 'cv-token-string';
                    if (/^"/.test(match)) {
                        className = /:\s*$/.test(match) ? 'cv-token-key' : 'cv-token-string';
                    } else if (/^(true|false)$/.test(match)) {
                        className = 'cv-token-boolean';
                    } else if (/^null$/.test(match)) {
                        className = 'cv-token-null';
                    } else if (/^-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?$/.test(match)) {
                        className = 'cv-token-number';
                    } else {
                        className = 'cv-token-punct';
                    }

                    return `<span class="cv-token ${className}">${match}</span>`;
                }
            );
        },
        highlightXml(value) {
            let next = value;

            next = next.replace(/(&lt;!--.*?--&gt;)/g, '<span class="cv-token cv-token-comment">$1</span>');
            next = next.replace(
                /(&lt;\/?)([A-Za-z_][A-Za-z0-9_.:-]*)(.*?)(\/?&gt;)/g,
                (_, open, tagName, attributes, close) => {
                    const attrs = attributes.replace(
                        /([A-Za-z_][A-Za-z0-9_.:-]*)(=)("[^"]*")/g,
                        '<span class="cv-token cv-token-attr">$1</span><span class="cv-token cv-token-punct">$2</span><span class="cv-token cv-token-string">$3</span>'
                    );

                    return `<span class="cv-token cv-token-punct">${open}</span><span class="cv-token cv-token-tag">${tagName}</span>${attrs}<span class="cv-token cv-token-punct">${close}</span>`;
                }
            );

            return next;
        },
    },
};
</script>

<style scoped>
.code-viewer {
    width: 100%;
    max-height: 72vh;
    overflow: auto;
    border: 1px solid var(--ui-border-subtle);
    border-radius: 0.375rem;
    background: #ffffff;
    font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-size: 0.82rem;
    line-height: 1.45;
}

.code-viewer--fill {
    height: 100%;
    max-height: none;
}

.code-viewer__line {
    display: grid;
    grid-template-columns: 56px minmax(0, 1fr);
    border-bottom: 1px solid var(--ui-surface-muted);
}

.code-viewer__line:last-child {
    border-bottom: 0;
}

.code-viewer__gutter {
    padding: 0.35rem 0.5rem;
    text-align: right;
    color: var(--ui-text-subtle);
    user-select: none;
    border-right: 1px solid var(--ui-border-subtle);
    background: var(--ui-surface-raised);
}

.code-viewer__code {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    padding: 0.35rem 0.75rem;
    color: var(--ui-text-heading);
}
</style>

<style>
.cv-token-key {
    color: #1d4ed8;
    font-weight: 600;
}

.cv-token-string {
    color: #047857;
}

.cv-token-number {
    color: #b45309;
}

.cv-token-boolean,
.cv-token-null {
    color: #9333ea;
    font-weight: 600;
}

.cv-token-punct {
    color: var(--ui-text-muted);
}

.cv-token-tag {
    color: #0f766e;
    font-weight: 600;
}

.cv-token-attr {
    color: #9a3412;
}

.cv-token-comment {
    color: var(--ui-text-muted);
    font-style: italic;
}
</style>
